import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/companies/FormField";
import { useLanguage } from "@/contexts/LanguageContext";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface EmailChangeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentEmail: string;
  newEmailAddress: string;
  isSendingInvitation: boolean;
  users: { id: string; email: string }[];
  onNewEmailChange: (value: string) => void;
  onEmailChangeSubmit: (newEmail: string) => Promise<void>;
  usersLoading?: boolean;
}

export const EmailChangeDialog = ({
  isOpen,
  onOpenChange,
  currentEmail,
  newEmailAddress,
  isSendingInvitation,
  users,
  onNewEmailChange,
  onEmailChangeSubmit,
  usersLoading,
}: EmailChangeDialogProps) => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    onOpenChange(false);
    onNewEmailChange("");
    setIsSubmitting(false);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onEmailChangeSubmit(newEmailAddress);
      // Close modal after successful submission
      handleClose();
    } catch (error) {
      // Error is already handled in the parent component
      console.error("Email change error:", error);
      setIsSubmitting(false);
    }
  };

  // Check if the new email is valid for submission
  const isNewEmailValid = newEmailAddress && 
                         newEmailAddress.trim() !== "" && 
                         newEmailAddress !== currentEmail &&
                         /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmailAddress);

  const isEmailUnique = !usersLoading && !users.find(user => user.email === newEmailAddress);
  const isEmailValidForSubmission = isNewEmailValid && isEmailUnique;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {t("companyForm.account.confirmEmailChangeTitle")}
          </DialogTitle>
          <DialogDescription>{t("companyForm.account.confirmEmailChangeDescription")}</DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-2">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg mb-4">
              <span className="text-sm text-gray-600">{t("companyForm.account.currentEmail")}</span>
              <span className="text-sm font-medium">{currentEmail}</span>
            </div>
            
            {/* Warning about invitation invalidation */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">{t("companyForm.account.confirmEmailChangeWarning")}</p>
                  <p className="text-xs opacity-90">
                    {t("companyForm.account.invitationInvalidationNotice")}
                  </p>
                  <p className="text-xs opacity-90 mt-1">
                    {t("companyForm.account.emailUniquenessNotice")}
                  </p>
                </div>
              </div>
            </div>
            
            <FormField
              label={t("companyForm.account.newEmail")}
              id="newEmail"
              type="email"
              value={newEmailAddress}
              onChange={(e) => onNewEmailChange(e.target.value)}
              onBlur={() => {}}
              validation={{ isValid: true, error: "", touched: false }}
              required={true}
            />

            {/* Real-time validation feedback */}
            {newEmailAddress && !isSubmitting && (
              <div className="mt-2">
                {newEmailAddress === currentEmail ? (
                  <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                    {t("companyForm.email.sameEmailError")}
                  </div>
                ) : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmailAddress) ? (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
                    {t("validation.company.email.format")}
                  </div>
                ) : !isEmailUnique ? (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
                    {t("companyForm.email.alreadyAssignedToOtherCompany")}
                  </div>
                ) : (
                  <div className="text-sm text-green-600 bg-green-50 p-2 rounded border border-green-200">
                    ✓ {t("companyForm.email.validEmail")}
                  </div>
                )}
              </div>
            )}

            {/* Confirmation summary when email is valid */}
            {isEmailValidForSubmission && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mt-4">
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-2">{t("companyForm.account.changeSummary")}</p>
                  <div className="space-y-1 text-xs">
                    <p>• {t("companyForm.account.oldEmailWillBeInvalidated")}: <span className="font-medium">{currentEmail}</span></p>
                    <p>• {t("companyForm.account.newInvitationWillBeSent")}: <span className="font-medium">{newEmailAddress}</span></p>
                    <p>• {t("companyForm.account.oldUserAccessRemoved")}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={handleClose}>
            {t("companyForm.buttons.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            disabled={isSendingInvitation || isSubmitting || !isEmailValidForSubmission}
            className="bg-red-600 hover:bg-red-700 text-white border-0"
          >
            {isSendingInvitation || isSubmitting ? t("companyForm.buttons.changingEmail") : t("companyForm.buttons.confirmEmailChange")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
